import { HttpDeprecatedService } from '@commons/core/transport/http-deprecated.service';
import { IHypervisionService } from '@hypervision/hypervision.service';
import { Domain } from '@hypervision/models/domain';
import { forEach, IPromise, isDefined, isUndefined } from 'angular';

import { ISkillViewFilterService } from './custom/filter/filter.service';

export interface ISkillViewService {
  // Retrieve domain List (returns the promise of an array of domains)
  getDomainList(): IPromise<Domain[]>;
  // In a list of domains, find the root and returns it (or null if not found)
  getRootDomain(domainList: Domain[]): Domain | null;
  // In a list of domains, find the root and returns it (or null if not found)
  getRootDomains(domainList: Domain[]): Domain[];
  // Retrieve skill List (returns a promise)
  getSkillList(rootDomainUuid);
  // Subscribe to metrics
  // metricQueries is an array of objects like { entityUuid: uuid, metricLabels: 'SUM_CTT_TRANSFERRED_SKILL' }
  cleanSubscribeToMetrics(skills, indicators, onMetricUpdateCallback, metricAccessor);
  // Subscribe to metrics
  // metricQueries is an array of objects like { entityUuid: uuid, metricLabels: 'SUM_CTT_TRANSFERRED_SKILL' }
  subscribeToMetrics(skills, indicators, onMetricUpdateCallback, metricAccessor);
  // when metric subscription is done
  subscribeToMetricsDone();
  // when metric subscription is done
  subscribeToMetricsFailed();
  // Unsubscribe metrics
  unsubscribeMetrics(callback);
  // when metrics unsubscription is done
  unsubscribeMetricsDone();
  // Build dataIndex and dataSet ()
  // - build an object from each row (for each skill) that contains every metric value and label key
  // - Add an element in the dataIndex (to ease access to the metric value : dataIndex['uuid@SUM_CSR_PRESENT_SKILL_NOW'].value)
  buildDataSetAndIndex(dataSet, dataIndex, skills, indicators, metricAccessor, viewName);
  // Build columns info
  buildColumnInfo(cols, indicators, metricAccessor);
}

export class SkillViewService implements ISkillViewService {
  static $inject: Array<string> = ['$q', 'httpDeprecated', 'deprecatedMetric', 'hypervision', 'skillFilterHypervision'];

  private metricsSubscribed;
  private metricUpdatedCallBack;
  private cachedDomainList: Domain[];

  constructor(
    private readonly $q,
    private readonly httpDeprecated: HttpDeprecatedService,
    private readonly metric,
    private readonly hypervision: IHypervisionService,
    private readonly skillFilterHypervision: ISkillViewFilterService
  ) {}

  getDomainList(): IPromise<Domain[]> {
    if (isUndefined(this.cachedDomainList)) {
      return this.hypervision.getDomainList().then((domains: Domain[]) => (this.cachedDomainList = domains));
    } else {
      return this.$q(resolve => resolve(this.cachedDomainList));
    }
  }

  getRootDomain(domainList: Domain[]): Domain | null {
    if (isDefined(domainList) && domainList.length > 0) {
      let domainUuids = domainList.map(d => d.uuid);
      let i;
      for (i = 0; i < domainList.length; i = i + 1) {
        if (domainUuids.indexOf(domainList[i].parentUuid) < 0) {
          return domainList[i];
        }
      }
    }
    return null;
  }

  getRootDomains(domainList: Domain[]): Domain[] {
    let rootDomains: Domain[] = [];
    let domainUuids: string[];
    if (isDefined(domainList) && domainList.length > 0) {
      domainUuids = domainList.map(d => d.uuid);
      for (let i = 0; i < domainList.length; i = i + 1) {
        if (domainUuids.indexOf(domainList[i].parentUuid) < 0) {
          rootDomains.push(domainList[i]);
        }
      }
    }
    return rootDomains;
  }

  getSkillList(rootDomainUuid) {
    return this.httpDeprecated
      .asPromise()
      .get('protected/administrator/getAllRecursiveSkills', [rootDomainUuid])
      .then(response => response.data);
  }

  // Build an array of informations for metric subscription
  // skills : [{uuid: 'uuid', ...}, ...]
  // indicators: [{name: 'name', ... }, ...]
  // metricAccessor : "name", "incomingPhoneName", .. (the accessor depends on the selected media)
  private buildMetricQueries(skills, indicators, metricAccessor) {
    let queries: any[] = [];
    let s: any;
    let m: any;
    let metricLabels: any[] = [];

    for (m = 0; m < indicators.length; m = m + 1) {
      if (indicators[m][metricAccessor] !== undefined) {
        metricLabels.push(indicators[m][metricAccessor]);
      }
    }
    for (s = 0; s < skills.length; s = s + 1) {
      queries.push({
        entityUuid: skills[s].uuid,
        metricLabels: metricLabels,
      });
    }
    return queries;
  }

  cleanSubscribeToMetrics(skills, indicators, onMetricUpdateCallback, metricAccessor) {
    // Some metric are already subscribed ?
    if (isDefined(this.metricsSubscribed) && this.metricsSubscribed.length > 0) {
      // perform unsubscription of previous metrics
      this.unsubscribeMetrics(() => {
        this.subscribeToMetrics(skills, indicators, onMetricUpdateCallback, metricAccessor);
      });
    } else {
      // perform the subscription
      this.subscribeToMetrics(skills, indicators, onMetricUpdateCallback, metricAccessor);
    }
  }

  subscribeToMetrics(skills, indicators, onMetricUpdateCallback, metricAccessor) {
    // Save the metrics and callback for later unsubscription
    this.metricsSubscribed = this.buildMetricQueries(skills, indicators, metricAccessor);
    this.metricUpdatedCallBack = onMetricUpdateCallback;

    // Subscribes
    this.metric
      .subscribe(this.metricsSubscribed, onMetricUpdateCallback, true)
      .then(this.subscribeToMetricsDone, this.subscribeToMetricsFailed);
  }

  subscribeToMetricsDone() {}

  subscribeToMetricsFailed() {}

  unsubscribeMetrics(callback) {
    if (isUndefined(callback)) {
      callback = this.unsubscribeMetricsDone;
    }
    this.metric.unsubscribe(this.metricsSubscribed, this.metricUpdatedCallBack).then(callback);
  }

  unsubscribeMetricsDone() {}

  buildDataSetAndIndex(dataSet, dataIndex, skills, indicators, metricAccessor, viewName) {
    // Empty the dataSet and the dataIndex
    dataSet.splice(0, dataSet.length);
    forEach(Object.keys(dataIndex), key => delete dataIndex[key]);

    // Fill the arrays
    let currentRow;
    let filter = this.skillFilterHypervision.getFilter(viewName);
    forEach(skills, currentSkill => {
      let filteredByMedia = isUndefined(filter) || this.skillFilterHypervision.isSkillApplicableToMedia(currentSkill, filter.mediaAccessor);
      if (filteredByMedia) {
        // Row data creation
        currentRow = {
          skillName: currentSkill.name,
          skillUuid: currentSkill.uuid,
          domainUuid: currentSkill.domain.domainUuid,
          mediaUuid: currentSkill.mediaUuid,
          show: true,
        };
        // populate currentRow with metric labels and initial value (undefined)
        forEach(indicators, currentMetric => {
          currentRow[currentMetric.label] = {
            metricLabel: currentMetric.label,
            value: undefined,
          };

          // Add metric part to the index
          dataIndex[currentSkill.uuid + '@' + currentMetric[metricAccessor]] = currentRow[currentMetric.label];
        });

        dataSet.push(currentRow);
      }
    });
  }

  buildColumnInfo(cols, indicators, metricAccessor) {
    // Empty the columns
    cols.splice(0, cols.length);

    // Recreate them
    forEach(indicators, currentMetric => {
      cols.push({
        label: currentMetric.label,
        title: currentMetric.label,
        name: currentMetric[metricAccessor],
        show: isDefined(currentMetric[metricAccessor]),
      });
    });
  }
}

export default 'skillHypervision';
